<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">
        <i class="fas fa-file-contract me-2"></i> {{ $t('contractType.title') }}
      </h4>

      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('contractType.code') }}</label>
              <input v-model="form.code" type="text" class="form-control" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t('contractType.name') }}</label>
              <input v-model="form.name" type="text" class="form-control" required />
            </div>
          </div>

          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded d-flex align-items-center justify-content-between">
              <label class="form-label mb-0">{{ $t('contractType.status') }}</label>
              <div class="form-check form-switch">
                <input v-model="form.is_active" class="form-check-input" type="checkbox" />
                <label class="form-check-label">{{ form.is_active ? $t('contractType.active') : $t('contractType.inactive') }}</label>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('contractType.update') : $t('contractType.save') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('contractType.cancel') }}</button>
        </div>
      </form>

      <!-- List -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
        <i class="fas fa-list me-2"></i> {{ $t('contractType.existingContractTypes') }}
      </h5>

      <div v-if="contractTypes.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('contractType.code') }}</th>
              <th>{{ $t('contractType.name') }}</th>
              <th>{{ $t('contractType.status') }}</th>
              <th class="text-center">{{ $t('contractType.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="type in contractTypes" :key="type.id">
              <td>{{ type.code }}</td>
              <td>{{ type.name }}</td>
              <td>
                <span :class="type.is_active ? 'text-success' : 'text-danger'">
                  {{ type.is_active ? $t('contractType.active') : $t('contractType.inactive') }}
                </span>
              </td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadType(type)">{{ $t('contractType.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm me-2" @click="deleteType(type.id)">{{ $t('contractType.delete') }}</button>
                <button class="btn btn-outline-dark btn-sm" @click="toggleStatus(type.id)">{{ $t('contractType.toggle') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('contractType.noContractTypes') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

import { useToast } from 'vue-toastification'
import contractTypeService from '@/services/contractTypeService'


const toast = useToast()

const contractTypes = ref([])
const editingId = ref(null)

const form = ref({
  code: '',
  name: '',
  is_active: true
})

const fetchContractTypes = async () => {
  try {
    const { data } = await contractTypeService.getAll()
    contractTypes.value = data
  } catch (err) {
    toast.error($t('contractType.failedToLoadContractTypes'))
    console.error(err)
  }
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await contractTypeService.update(editingId.value, form.value)
      toast.success($t('contractType.contractTypeUpdated'))
    } else {
      await contractTypeService.create(form.value)
      toast.success($t('contractType.contractTypeCreated'))
    }
    resetForm()
    fetchContractTypes()
  } catch (err) {
    toast.error($t('contractType.failedToSaveContractType'))
    console.error(err)
  }
}

const loadType = (type) => {
  editingId.value = type.id
  form.value = {
    code: type.code,
    name: type.name,
    is_active: type.is_active
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    code: '',
    name: '',
    is_active: true
  }
}

const deleteType = async (id) => {
  if (!confirm($t('contractType.confirmDelete'))) return
  try {
    await contractTypeService.delete(id)
    contractTypes.value = contractTypes.value.filter(t => t.id !== id)
    toast.success($t('contractType.contractTypeDeleted'))
  } catch (err) {
    toast.error($t('contractType.failedToDeleteContractType'))
    console.error(err)
  }
}

const toggleStatus = async (id) => {
  try {
    await contractTypeService.toggleStatus(id)
    const target = contractTypes.value.find(t => t.id === id)
    if (target) target.is_active = !target.is_active
    toast.success($t('contractType.statusUpdated'))
  } catch (err) {
    toast.error($t('contractType.failedToUpdateStatus'))
    console.error(err)
  }
}

onMounted(() => {
  fetchContractTypes()
})
</script>
